import { InstructionType, RedistributionImportInputData } from './redistributionImportTypes';
import {
  ImportedItemRow,
  ImportedItemValidationResult,
  ImportedItemValidator,
  ImportedItemValidatorObject,
  ImportFormatItem,
} from '../../../../../lib/imports/importTypes';
import { createValidColumnsCountValidator } from '../../../../../lib/imports/importValidatorsLib';
import { ValidationProblemCode } from '@ydistri/api-sdk';
import { messageFor } from '../../../../Configuration/Validator/problemsLib';

const isPositiveNumber = (value: unknown): value is number => {
  return typeof value === 'number' && value > 0;
};

const isNegativeNumber = (value: unknown): value is number => {
  return typeof value === 'number' && value < 0;
};

/**
 * Checks if the source and target store identifiers are the same.
 * @param item
 */
export const identicalStoreAndTargetValidator = (
  item: ImportedItemRow<RedistributionImportInputData>,
): ImportedItemValidationResult => {
  const data = item.inputData;
  if (data.sourceStoreIdentifier === data.targetStoreIdentifier) {
    return [messageFor(ValidationProblemCode.RecordImportSameSourceAndTargetStore)];
  }
};

/**
 * Checks if the value is a whole number.
 * @param value number to validate
 * @return error message if the value is not a whole number, otherwise undefined
 */
const validateWholeNumber = (value: number): string | undefined => {
  if (!Number.isInteger(value)) {
    return 'Invalid quantity - must be a whole number';
  }
};

/**
 * Creates a validator for checking invalid values.
 * Quantity and Description depend on the instruction type.
 * @param instructionType
 */
export const createInvalidValuesValidator = (
  instructionType: InstructionType,
): ImportedItemValidator<RedistributionImportInputData> => {
  return (item: ImportedItemRow<RedistributionImportInputData>): ImportedItemValidationResult => {
    const result: string[] = [];
    const data = item.inputData;

    if (!data.productIdentifier) {
      result.push('Product identifier must not be empty.');
    }

    if (!data.sourceStoreIdentifier) {
      result.push('Source store identifier must not be empty.');
    }

    if (!data.targetStoreIdentifier) {
      result.push('Target store identifier must not be empty.');
    }

    switch (instructionType) {
      case InstructionType.QUANTITY:
        if (isNegativeNumber(data.count)) {
          result.push(messageFor(ValidationProblemCode.RecordImportNegativeQuantity));
        } else if (isPositiveNumber(data.count)) {
          const wholeNumberError = validateWholeNumber(data.count);
          if (wholeNumberError) {
            result.push(wholeNumberError);
          }
        } else {
          // 0, empty or not a number
          result.push('Invalid quantity - must be a positive number.');
        }
        break;
      case InstructionType.QUANTITY_DESCRIPTION:
        // when description is provided, count may be empty or 0
        // if count is provided, it must be a positive number
        // if description is empty, count must be a positive number
        if (!data.description || data.description.trim() === '') {
          if (isNegativeNumber(data.count)) {
            result.push(messageFor(ValidationProblemCode.RecordImportNegativeQuantity));
          } else if (isPositiveNumber(data.count)) {
            const wholeNumberError = validateWholeNumber(data.count);
            if (wholeNumberError) {
              result.push(wholeNumberError);
            }
          } else {
            // 0, empty or not a number
            result.push('Invalid quantity - must be greater than 0 when description is empty.');
          }
        } else {
          if (isNegativeNumber(data.count)) {
            result.push(messageFor(ValidationProblemCode.RecordImportNegativeQuantity));
          }
        }
        break;
      case InstructionType.DESCRIPTION:
        if (!data.description || data.description.trim() === '') {
          result.push('Description must not be empty.');
        }
        break;
    }

    if (result.length > 0) {
      return result;
    }
  };
};

/**
 * Rows may contain duplicate data which is rows with the same product, source and target store identifiers.
 * This validator marks rows that are such duplicates.
 * @param rows
 * @param identificationKeys keys that should be used to identify the row
 */
export class DuplicateItemsValidator implements ImportedItemValidatorObject<RedistributionImportInputData> {
  private keyMap: Record<string, number> = {};

  validate(item: ImportedItemRow<RedistributionImportInputData>): ImportedItemValidationResult {
    const data = item.inputData;
    const rowKey = `${data.productIdentifier}-${data.sourceStoreIdentifier}-${data.targetStoreIdentifier}`;
    if (this.keyMap[rowKey]) {
      return [`Is duplicate to row ${this.keyMap[rowKey]}`];
    } else {
      this.keyMap[rowKey] = item.rowNumber;
    }
  }

  reset(): void {
    this.keyMap = {};
  }
}

/**
 * Product cannot be used as a source and target at the same store. Only rows the rules before this
 * one accepted take part - a row already refused neither clashes nor claims its stores - which is
 * the server's order of decisions (the store rule runs last, over the rows still importable).
 * Advisory: the server decides on resolved products and stores, and a row whose identifier does not
 * resolve claims nothing there, which this copy cannot know - so it warns and lets the row through.
 */
export class SameSourceAndTargetValidator implements ImportedItemValidatorObject<RedistributionImportInputData> {
  readonly advisory = true;
  private sources: Record<string, number | undefined> = {};
  private targets: Record<string, number | undefined> = {};

  validate(item: ImportedItemRow<RedistributionImportInputData>): ImportedItemValidationResult {
    if (item.errors !== undefined && item.errors.length > 0) return undefined;
    const result: string[] = [];
    const productId = item.inputData.productIdentifier;
    const sourceStoreId = item.inputData.sourceStoreIdentifier;
    const targetStoreId = item.inputData.targetStoreIdentifier;

    const sourceKey = `${productId}-${sourceStoreId}`;
    const targetKey = `${productId}-${targetStoreId}`;

    // One server code for both directions; the row it clashes with is the useful part.
    const sourceUsedAsTarget = this.targets[sourceKey];
    if (sourceUsedAsTarget) {
      result.push(
        `${messageFor(ValidationProblemCode.RecordImportSkuUsedAsSourceAndTarget)} (source store was the target on line ${sourceUsedAsTarget}; the server decides once the stores are resolved)`,
      );
    }

    const targetUsedAsSource = this.sources[targetKey];
    if (targetUsedAsSource) {
      result.push(
        `${messageFor(ValidationProblemCode.RecordImportSkuUsedAsSourceAndTarget)} (target store was the source on line ${targetUsedAsSource}; the server decides once the stores are resolved)`,
      );
    }

    this.sources[sourceKey] = item.rowNumber;
    this.targets[targetKey] = item.rowNumber;

    if (result.length > 0) {
      return result;
    }
  }

  reset(): void {
    this.sources = {};
    this.targets = {};
  }
}

export const createRedistributionImportValidators = (
  instructionSpecification: InstructionType,
  importFormat: ImportFormatItem<RedistributionImportInputData>[],
): ImportedItemValidator<RedistributionImportInputData>[] => {
  return [
    createInvalidValuesValidator(instructionSpecification),
    createValidColumnsCountValidator(importFormat),
    identicalStoreAndTargetValidator,
    new DuplicateItemsValidator(),
    new SameSourceAndTargetValidator(),
  ];
};
